import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { LookupSelect } from './LookupSelect';
import {
  addressToQuery,
  addressValueFromComponents,
  fillBlankAddressFields,
  isAddressEmpty,
  type AddressValue,
} from '../utils/address';
import './ContactListInput.css';

interface GeocodeComponents {
  house_number?: string | null;
  street?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}
interface GeocodeResponse {
  configured: boolean;
  ok?: boolean;
  formatted?: string | null;
  confidence?: number;
  components?: GeocodeComponents | null;
}

type VerifyState = { status: 'idle' | 'loading' | 'ok' | 'warn'; msg: string };

interface AddressFieldsProps {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  disabled?: boolean;
  /** A legacy single-line address to auto-parse into the structured fields on
   *  first open (only when `autoParse` is set and the value is still empty). */
  legacyText?: string | null;
  autoParse?: boolean;
  /** aria-label prefix when several fieldsets share a page. */
  idPrefix?: string;
}

/** One structured US address: No. / street / type · suite · city / state / ZIP,
 *  with a Geoapify "Verify address" button. Used standalone (single address)
 *  and inside <AddressListInput> (one per row). */
export function AddressFields({
  value, onChange, disabled = false, legacyText, autoParse = false, idPrefix = 'addr',
}: AddressFieldsProps): JSX.Element {
  const [verify, setVerify] = useState<VerifyState | null>(null);
  const parsedOnce = useRef(false);
  // Keep the latest value for async geocode callbacks so we never overwrite
  // fields the user typed while a request was in flight.
  const valueRef = useRef(value);
  valueRef.current = value;
  const set = (patch: Partial<AddressValue>) => onChange({ ...value, ...patch });

  // Auto-parse a legacy free-text address into the fields once, via Geoapify.
  // Degrades to seeding the street name with the raw text if geocode is off.
  // If the user has already started typing, only blank fields are filled.
  useEffect(() => {
    if (!autoParse || parsedOnce.current) return;
    const seed = (legacyText ?? '').trim();
    if (seed.length === 0 || !isAddressEmpty(value)) return;
    parsedOnce.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<GeocodeResponse>(`/api/geocode?q=${encodeURIComponent(seed)}`);
        if (cancelled) return;
        const current = valueRef.current;
        if (res.configured && res.components) {
          const parsed = addressValueFromComponents(res.components);
          onChange(isAddressEmpty(current) ? parsed : fillBlankAddressFields(current, parsed));
          setVerify({ status: 'ok', msg: 'Parsed from the saved address — review below.' });
        } else if (isAddressEmpty(current)) {
          onChange({ ...current, streetName: seed });
        } else if (!current.streetName.trim()) {
          onChange({ ...current, streetName: seed });
        }
      } catch {
        if (cancelled) return;
        const current = valueRef.current;
        if (isAddressEmpty(current)) onChange({ ...current, streetName: seed });
        else if (!current.streetName.trim()) onChange({ ...current, streetName: seed });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoParse, legacyText]);

  const onVerify = async () => {
    const q = addressToQuery(value);
    if (q.length === 0) { setVerify({ status: 'warn', msg: 'Enter an address first.' }); return; }
    setVerify({ status: 'loading', msg: 'Checking…' });
    try {
      const res = await api.get<GeocodeResponse>(`/api/geocode?q=${encodeURIComponent(q)}`);
      if (!res.configured) {
        setVerify({ status: 'warn', msg: 'Address verification is not configured.' });
        return;
      }
      if (!res.ok || !res.components) {
        setVerify({ status: 'warn', msg: '⚠ Could not verify this address.' });
        return;
      }
      // Successful verify: fill any blank fields (city/state/ZIP/etc.) from the
      // geocoder result without overwriting what the user already entered.
      const filled = fillBlankAddressFields(valueRef.current, addressValueFromComponents(res.components));
      onChange(filled);
      setVerify({ status: 'ok', msg: `✓ ${res.formatted ?? 'Verified'}` });
    } catch {
      setVerify({ status: 'warn', msg: '⚠ Verification failed.' });
    }
  };

  // Browser address autofill fights split street fields (No. / name / type) and
  // can dump characters into the wrong box. Keep it off for this composite.
  const ac = 'off' as const;

  return (
    <div className="addr-block">
      <div className="addr-row">
        <input
          type="text"
          className="form-input addr-num"
          placeholder="No."
          value={value.houseNumber}
          onChange={(e) => set({ houseNumber: e.target.value })}
          disabled={disabled}
          maxLength={20}
          autoComplete={ac}
          name={`${idPrefix}-house`}
          aria-label={`${idPrefix} house number`}
        />
        <LookupSelect
          category="street_direction"
          className="form-input addr-dir"
          value={value.streetDirection}
          onChange={(v) => set({ streetDirection: v })}
          disabled={disabled}
          allowEmpty
          emptyLabel="Dir"
          optionText="value"
          aria-label={`${idPrefix} street direction`}
        />
        <input
          type="text"
          className="form-input contact-list-grow"
          placeholder="Street name"
          value={value.streetName}
          onChange={(e) => set({ streetName: e.target.value })}
          disabled={disabled}
          maxLength={150}
          autoComplete={ac}
          name={`${idPrefix}-street`}
          aria-label={`${idPrefix} street name`}
        />
        <LookupSelect
          category="street_type"
          className="form-input addr-type"
          value={value.streetType}
          onChange={(v) => set({ streetType: v })}
          disabled={disabled}
          allowEmpty
          emptyLabel="— St. type —"
          aria-label={`${idPrefix} street type`}
        />
        <LookupSelect
          category="street_direction"
          className="form-input addr-dir"
          value={value.streetPostDirection}
          onChange={(v) => set({ streetPostDirection: v })}
          disabled={disabled}
          allowEmpty
          emptyLabel="Dir"
          optionText="value"
          aria-label={`${idPrefix} street post-direction`}
        />
      </div>

      <input
        type="text"
        className="form-input"
        placeholder="Apt, suite, unit (optional)"
        value={value.line2}
        onChange={(e) => set({ line2: e.target.value })}
        disabled={disabled}
        maxLength={200}
        autoComplete={ac}
        name={`${idPrefix}-line2`}
        aria-label={`${idPrefix} line 2`}
      />

      <div className="addr-row">
        <input
          type="text"
          className="form-input contact-list-grow"
          placeholder="City"
          value={value.city}
          onChange={(e) => set({ city: e.target.value })}
          disabled={disabled}
          maxLength={100}
          autoComplete={ac}
          name={`${idPrefix}-city`}
          aria-label={`${idPrefix} city`}
        />
        <LookupSelect
          category="us_state"
          className="form-input addr-state"
          value={value.state}
          onChange={(v) => set({ state: v })}
          disabled={disabled}
          allowEmpty
          emptyLabel="State"
          optionText="value"
          aria-label={`${idPrefix} state`}
        />
        <input
          type="text"
          className="form-input addr-zip"
          placeholder="ZIP"
          value={value.postal_code}
          onChange={(e) => set({ postal_code: e.target.value })}
          disabled={disabled}
          maxLength={20}
          autoComplete={ac}
          name={`${idPrefix}-zip`}
          aria-label={`${idPrefix} ZIP`}
        />
      </div>

      <div className="addr-verify">
        <button
          type="button" className="btn-charcoal-secondary addr-verify-btn"
          onClick={onVerify} disabled={disabled || verify?.status === 'loading'}
        >
          {verify?.status === 'loading' ? 'Checking…' : 'Verify address'}
        </button>
        {verify && verify.status !== 'loading' && (
          <span className={`addr-verify-result addr-verify-${verify.status}`}>{verify.msg}</span>
        )}
      </div>
    </div>
  );
}
